import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { trackDownload, trackPlatformSelected } from "../lib/analytics";
import { apiJson } from "../lib/api";
import {
  Download as DownloadIcon,
  Monitor,
  Apple,
  Smartphone,
  Shield,
  Zap,
  Lock,
  Globe,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Cpu,
  HardDrive,
  Wifi,
  Eye,
  Server,
  Terminal,
  ExternalLink,
  Clock,
  ArrowRight,
  X,
  Bell,
  Mail,
} from "lucide-react";

// Platform detection
function detectPlatform() {
  if (typeof window === "undefined") return "windows";
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("mac")) return "macos";
  if (ua.includes("linux")) return "linux";
  if (ua.includes("android")) return "android";
  if (ua.includes("iphone") || ua.includes("ipad")) return "ios";
  return "windows";
}

const platforms = {
  windows: {
    name: "Windows",
    icon: Monitor,
    version: "1.1.0",
    size: "3 MB",
    requirements: "Windows 10/11 (64-bit)",
    downloadUrl: "https://github.com/kstephens0331/sacvpn-releases/releases/latest/download/SACVPN_x64-setup.exe",
    downloadUrlMsi: "https://github.com/kstephens0331/sacvpn-releases/releases/latest/download/SACVPN_x64_en-US.msi",
    fileName: "SACVPN_x64-setup.exe",
    featured: true,
  },
  macos: {
    name: "macOS",
    icon: Apple,
    version: "1.0.0",
    size: "42 MB",
    requirements: "macOS 10.15+",
    downloadUrl: null, // Set to actual URL when builds are ready
    fileName: "SACVPN-1.0.0.dmg",
    featured: true,
  },
  linux: {
    name: "Linux",
    icon: Terminal,
    version: "1.0.0",
    size: "38 MB",
    requirements: "Ubuntu 20.04+, Debian 11+, Fedora 35+",
    downloadUrl: null, // Set to actual URL when builds are ready
    fileName: "sacvpn-1.0.0.deb",
    featured: false,
  },
  android: {
    name: "Android",
    icon: Smartphone,
    version: "1.0.0",
    size: "28 MB",
    requirements: "Android 8.0+",
    downloadUrl: null,
    fileName: "SACVPN-1.0.0.apk",
    featured: false,
    comingSoon: true,
  },
  ios: {
    name: "iOS",
    icon: Apple,
    version: "1.0.0",
    size: "32 MB",
    requirements: "iOS 14+",
    downloadUrl: null,
    fileName: "App Store",
    featured: false,
    comingSoon: true,
  },
};

const features = [
  {
    icon: Zap,
    title: "WireGuard protocol",
    description: "A modern, lightweight tunnel with a light footprint on every connection.",
  },
  {
    icon: Shield,
    title: "Strong encryption",
    description: "ChaCha20 encryption protects your data from snooping and interception.",
  },
  {
    icon: Eye,
    title: "No-logs policy",
    description: "We never track, store, or share your browsing activity. Ever.",
  },
  {
    icon: Globe,
    title: "US network",
    description: "3 US nodes in Texas, Virginia, and Dallas for private access, with more regions as demand grows.",
  },
  {
    icon: Lock,
    title: "Kill switch",
    description: "Optional WireGuard app setting that blocks traffic if the connection drops.",
  },
  {
    icon: Server,
    title: "Split tunneling",
    description: "Choose which apps use the VPN and which use your regular connection.",
  },
];

const installSteps = {
  windows: [
    { step: 1, title: "Download", description: "Click the download button to get the installer" },
    { step: 2, title: "Run installer", description: "Double-click the .exe installer and follow the prompts" },
    { step: 3, title: "Sign in", description: "Launch SACVPN and sign in with your account" },
    { step: 4, title: "Connect", description: "Click the connect button to secure your connection" },
  ],
  macos: [
    { step: 1, title: "Download", description: "Click the download button to get the DMG file" },
    { step: 2, title: "Install", description: "Open the DMG and drag SACVPN to Applications" },
    { step: 3, title: "Authorize", description: "Allow the system extension in Security preferences" },
    { step: 4, title: "Connect", description: "Launch SACVPN, sign in, and connect" },
  ],
  linux: [
    { step: 1, title: "Download", description: "Download the .deb or .rpm package" },
    { step: 2, title: "Install", description: "Run: sudo dpkg -i sacvpn.deb" },
    { step: 3, title: "Configure", description: "Run: sacvpn login" },
    { step: 4, title: "Connect", description: "Run: sacvpn connect" },
  ],
};

export default function Download() {
  const [detectedPlatform, setDetectedPlatform] = useState("windows");
  const [selectedPlatform, setSelectedPlatform] = useState("windows");
  const [showAllPlatforms, setShowAllPlatforms] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadingPlatform, setDownloadingPlatform] = useState(null);
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [notifyError, setNotifyError] = useState("");

  useEffect(() => {
    const platform = detectPlatform();
    setDetectedPlatform(platform);
    setSelectedPlatform(platform);
  }, []);

  // Handle download click
  const handleDownload = (platformKey) => {
    const platform = platforms[platformKey];

    if (platform.comingSoon) {
      return; // Already handled by "Coming Soon" button
    }

    if (platform.downloadUrl) {
      // Track the download event
      const fileExt = platform.fileName?.split('.').pop() || 'exe';
      trackDownload(platform.name, fileExt);
      // If we have a real download URL, trigger the download
      window.open(platform.downloadUrl, '_blank');
    } else {
      // Show modal that download is being prepared
      setNotifySubmitted(false);
      setNotifyError("");
      setDownloadingPlatform(platformKey);
      setShowDownloadModal(true);
    }
  };

  const currentPlatform = platforms[selectedPlatform];
  const PlatformIcon = currentPlatform.icon;

  // Auto-advance installation steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>Download Business VPN Client - Windows, Mac, Linux | SACVPN</title>
        <meta
          name="description"
          content="Download the SACVPN business VPN client for Windows, macOS, and Linux. WireGuard-based secure remote access, centralized team deployment, and a 14-day free trial for businesses."
        />
        <meta
          name="keywords"
          content="download business VPN, enterprise VPN client, corporate VPN download, Windows VPN client, Mac VPN client, team VPN deployment"
        />
        <link rel="canonical" href="https://www.sacvpn.com/download" />
        <meta property="og:title" content="Download Business VPN Client - Windows, Mac, Linux | SACVPN" />
        <meta property="og:description" content="Download the SACVPN business VPN client for Windows, macOS, and Linux. WireGuard-based secure remote access, centralized team deployment, and a 14-day free trial for businesses." />
      </Helmet>

      <div className="bg-dark-900">
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bg-dark-900" />
          <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)`,
              backgroundSize: "64px 64px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6">
            {/* Main Heading */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Download</p>
              <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight">
                Download <span className="text-brand-400">SACVPN</span>
              </h1>
              <p className="mt-6 text-lg text-gray-400 leading-relaxed">
                Protect your privacy with a VPN client powered by WireGuard, for every device you use.
              </p>
            </motion.div>

            {/* Main Download Card */}
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="max-w-2xl mx-auto"
            >
              <div className="rounded-xl border border-white/[0.08] bg-dark-900 overflow-hidden">
                {/* Detected Platform Banner */}
                {detectedPlatform === selectedPlatform && (
                  <div className="bg-brand-500/[0.06] px-6 py-3 border-b border-brand-500/20">
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-brand-400" />
                      <span className="text-gray-400">
                        We detected you're using <span className="text-white font-semibold">{currentPlatform.name}</span>
                      </span>
                    </div>
                  </div>
                )}

                <div className="p-8 md:p-10">
                  {/* Platform Icon & Info */}
                  <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-16 h-16 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-6">
                      <PlatformIcon className="w-8 h-8 text-brand-400" />
                    </div>
                    <h2 className="text-2xl font-display font-bold text-white tracking-tight mb-2">
                      SACVPN for {currentPlatform.name}
                    </h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Version {currentPlatform.version}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600" />
                      <span>{currentPlatform.size}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600" />
                      <span>{currentPlatform.requirements}</span>
                    </div>
                  </div>

                  {/* Download Button */}
                  {currentPlatform.comingSoon ? (
                    <div className="mb-6">
                      <button
                        disabled
                        className="w-full py-4 rounded-lg bg-white/5 border border-white/10 text-gray-500 font-semibold text-lg cursor-not-allowed"
                      >
                        Coming soon
                      </button>
                      <p className="text-center text-gray-500 text-sm mt-3">
                        Mobile apps are currently in development
                      </p>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <button
                        onClick={() => handleDownload(selectedPlatform)}
                        className="w-full py-4 rounded-lg bg-brand-500 hover:bg-brand-400 text-white font-semibold text-lg transition-colors flex items-center justify-center gap-3"
                      >
                        <DownloadIcon className="w-6 h-6" />
                        Download for {currentPlatform.name}
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      <p className="text-center text-gray-500 text-sm mt-3">
                        14-day free trial. No credit card required.
                      </p>
                      {/* Enterprise MSI option for Windows */}
                      {selectedPlatform === "windows" && currentPlatform.downloadUrlMsi && (
                        <div className="mt-4 pt-4 border-t border-white/[0.08]">
                          <p className="text-center text-gray-400 text-sm mb-2">
                            <strong className="text-white">Enterprise deployment?</strong>
                          </p>
                          <a
                            href={currentPlatform.downloadUrlMsi}
                            className="flex items-center justify-center gap-2 text-brand-400 hover:text-brand-300 text-sm transition-colors"
                          >
                            <HardDrive className="w-4 h-4" />
                            Download MSI for Group Policy / SCCM
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/[0.08]">
                    {[
                      { label: "Setup time", value: "< 2 min", icon: Clock },
                      { label: "Protocol", value: "WireGuard", icon: Zap },
                      { label: "Encryption", value: "ChaCha20", icon: Lock },
                    ].map((stat) => (
                      <div key={stat.label} className="text-center">
                        <stat.icon className="w-5 h-5 text-brand-400 mx-auto mb-2" />
                        <div className="text-white font-semibold">{stat.value}</div>
                        <div className="text-gray-500 text-xs">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Other Platforms Toggle */}
              <button
                onClick={() => setShowAllPlatforms(!showAllPlatforms)}
                className="w-full mt-6 py-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white font-medium transition-colors flex items-center justify-center gap-2"
              >
                {showAllPlatforms ? "Hide" : "Show"} all platforms
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${showAllPlatforms ? "rotate-180" : ""}`}
                />
              </button>

              {/* All Platforms Grid */}
              <AnimatePresence>
                {showAllPlatforms && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3 overflow-hidden"
                  >
                    {Object.entries(platforms).map(([key, platform]) => {
                      const Icon = platform.icon;
                      const isSelected = selectedPlatform === key;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setSelectedPlatform(key);
                            trackPlatformSelected(platform.name);
                          }}
                          className={`p-4 rounded-lg border transition-colors ${
                            isSelected
                              ? "bg-brand-500/10 border-brand-500/40 text-white"
                              : "bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                          }`}
                        >
                          <Icon className="w-6 h-6 mx-auto mb-2" />
                          <div className="text-sm font-medium">{platform.name}</div>
                          {platform.comingSoon && (
                            <div className="text-xs text-gray-500 mt-1">Soon</div>
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 bg-dark-900">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="max-w-2xl mb-14"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Why SACVPN</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Built for privacy and simplicity
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <feature.icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Installation Guide */}
        <section className="py-24 bg-dark-800/40 border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="max-w-2xl mb-14"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Setup</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Get started in minutes
              </h2>
            </motion.div>

            <div className="max-w-5xl">
              <div className="grid md:grid-cols-4 gap-4">
                {(installSteps[selectedPlatform] || installSteps.windows).map((item, i) => (
                  <div
                    key={item.step}
                    className={`relative p-6 rounded-xl border transition-colors ${
                      activeStep === i
                        ? "bg-brand-500/[0.06] border-brand-500/40"
                        : "bg-dark-900 border-white/[0.08]"
                    }`}
                  >
                    {/* Step Number */}
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 font-mono text-sm ${
                        activeStep === i
                          ? "bg-brand-500 text-white"
                          : "bg-white/5 text-gray-400"
                      }`}
                    >
                      {item.step}
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>

                    {/* Connector Line */}
                    {i < 3 && (
                      <div className="hidden md:block absolute top-12 -right-2 w-4 h-px bg-white/10" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* System Requirements */}
        <section className="py-24 bg-dark-900">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="max-w-2xl mb-14"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Requirements</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                System requirements
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Windows */}
              <div className="p-8 rounded-xl bg-dark-900 border border-white/[0.08]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Windows</h3>
                </div>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-start gap-2.5">
                    <Cpu className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>Windows 10 or 11 (64-bit)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <HardDrive className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>100 MB free disk space</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Wifi className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>Active internet connection</span>
                  </li>
                </ul>
              </div>

              {/* macOS */}
              <div className="p-8 rounded-xl bg-dark-900 border border-white/[0.08]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Apple className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">macOS</h3>
                </div>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-start gap-2.5">
                    <Cpu className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>macOS 10.15 (Catalina) or later</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <HardDrive className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>100 MB free disk space</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Wifi className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>Apple Silicon or Intel Mac</span>
                  </li>
                </ul>
              </div>

              {/* Linux */}
              <div className="p-8 rounded-xl bg-dark-900 border border-white/[0.08]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Terminal className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Linux</h3>
                </div>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-start gap-2.5">
                    <Cpu className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>Ubuntu 20.04+, Debian 11+, Fedora 35+</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <HardDrive className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>100 MB free disk space</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Wifi className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span>systemd-based distribution</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Download FAQ */}
        <section className="py-24 bg-dark-800/40 border-y border-white/5">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="mb-12 text-center"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Download FAQ</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Questions before you install
              </h2>
            </motion.div>

            <div className="space-y-4">
              {[
                {
                  q: "Is the SACVPN download free?",
                  a: "Yes. The installer itself is free, and every new account starts with a 14-day free trial, no credit card required.",
                },
                {
                  q: "Do I need an account before I download?",
                  a: "No. Download the installer first, then sign in or start your trial the first time you launch the app. For billing and account questions, see our full FAQ page.",
                  linkTo: "/faq",
                  linkLabel: "full FAQ page",
                },
                {
                  q: "Which download do I need for a business rollout?",
                  a: "Individual installs on Windows use the standard .exe installer above. For deploying to a fleet of machines with Group Policy or SCCM, use the MSI package linked under the Windows download.",
                },
                {
                  q: "New to VPNs? Where do I start?",
                  a: "Our step-by-step setup guide for beginners walks through installing and connecting on Windows, macOS, iPhone, Android, and Linux.",
                  linkTo: "/blog/vpn-setup-guide-beginners",
                  linkLabel: "step-by-step setup guide for beginners",
                },
              ].map((item, i) => (
                <details key={i} className="group rounded-xl border border-white/[0.08] bg-dark-900 p-6">
                  <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white">
                    <span>{item.q}</span>
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <p className="text-gray-400 mt-4 leading-relaxed">
                    {item.linkTo ? (
                      <>
                        {item.a.split(item.linkLabel)[0]}
                        <Link to={item.linkTo} className="text-brand-400 underline decoration-brand-500/40 underline-offset-2 hover:text-brand-300 transition-colors">
                          {item.linkLabel}
                        </Link>
                        {item.a.split(item.linkLabel)[1]}
                      </>
                    ) : (
                      item.a
                    )}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-dark-900 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
                Ready to protect your privacy?
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
                Put your traffic on a private WireGuard network. Start your 14-day free trial, no credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => handleDownload(selectedPlatform)}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <DownloadIcon className="w-5 h-5" />
                  Download now
                </button>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  View plans
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
              <p className="text-gray-500 mt-6 text-sm">
                14-day free trial. No credit card required. Cancel anytime.
              </p>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Download Coming Soon Modal */}
      <AnimatePresence>
        {showDownloadModal && downloadingPlatform && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowDownloadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-dark-900 rounded-xl border border-white/[0.08] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
                <h3 className="text-xl font-semibold text-white">Download coming soon</h3>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    {(() => {
                      const PlatformModalIcon = platforms[downloadingPlatform].icon;
                      return <PlatformModalIcon className="w-7 h-7 text-brand-400" />;
                    })()}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">
                      SACVPN for {platforms[downloadingPlatform].name}
                    </h4>
                    <p className="text-gray-500 text-sm">
                      Version {platforms[downloadingPlatform].version} · {platforms[downloadingPlatform].size}
                    </p>
                  </div>
                </div>

                <div className="bg-brand-500/[0.06] border border-brand-500/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium mb-1">
                        We're putting the finishing touches on the {platforms[downloadingPlatform].name} app.
                      </p>
                      <p className="text-gray-400 text-sm">
                        Our desktop clients are in final testing. Leave your email to be notified when the download is available.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email Signup */}
                {notifySubmitted ? (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-brand-500/[0.06] border border-brand-500/20">
                    <CheckCircle className="w-5 h-5 text-brand-400 flex-shrink-0" />
                    <p className="text-white text-sm">
                      You're on the list. We'll email you when SACVPN for {platforms[downloadingPlatform].name} is ready.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const email = e.target.email.value;
                      setNotifySubmitting(true);
                      setNotifyError("");
                      try {
                        await apiJson("/api/download/notify", {
                          method: "POST",
                          body: JSON.stringify({ email, platform: downloadingPlatform }),
                        });
                        setNotifySubmitted(true);
                      } catch (err) {
                        setNotifyError(err.message || "Something went wrong. Please try again.");
                      } finally {
                        setNotifySubmitting(false);
                      }
                    }}
                    className="space-y-4"
                  >
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        required
                        className="w-full pl-12 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 transition-colors"
                      />
                    </div>
                    {notifyError && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                        {notifyError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={notifySubmitting}
                      className="w-full py-3 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {notifySubmitting ? "Submitting..." : "Notify me when available"}
                    </button>
                  </form>
                )}

                <p className="text-center text-gray-500 text-xs mt-4">
                  No spam. We'll only email you when the download is ready.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
